import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import { toast } from 'react-toastify';
import EntitySearchPicker, { getEntityDisplayName, getEntityId } from '../components/EntitySearchPicker';
import { loadDropdownData, addAddressToEntity, getAllContactsByEntity, setContactDeviceAccess, createAdditionalContact, batchAddContacts, loadEntityAddressItems } from '../customer/onboardingApi';
import { Card, CardContent, FormControlLabel, Grid, IconButton, MenuItem, Switch, TextField } from '@mui/material';
import StepBar from '../components/StepBar';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import ContactPrefillDialog from './ai/ContactPrefillDialog';
import { applyContactCandidates } from './ai/contactPrefillAdapter';
import crudsService from 'services/cruds-service';


// ── Steps ─────────────────────────────────────────────────────
const STEPS = [
    { key: 'search', label: 'Find Entity' },
    { key: 'contacts', label: 'Contacts' },
    { key: 'review', label: 'Review' },
    { key: 'permissions', label: 'Permissions' },
    { key: 'done', label: 'Done' },
];

// ── Helpers ───────────────────────────────────────────────────
function maskPhone(value) {
    const digits = value.replace(/\D/g, '').slice(0, 10);
    if (!digits.length) return '';
    if (digits.length <= 3) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

/**
 * Blank contacts initialization
 */
function blankContact(activeStatusId) {
    return {
        id: crypto.randomUUID(),
        contact_first_name: '',
        contact_last_name: '',
        contact_type_id: 0,
        contact_status_type_id: activeStatusId,
        email: '',
        phone: '',
        title: '',
        createUserAccount: false,
    };
}

/**
 * Get the ID of the "Active" status from dropdown data
 */
function getActiveStatusId(dropdowns) {
    if (!dropdowns || !dropdowns.statusTypes) return '';
    return (
        dropdowns.statusTypes.find(
            (s) => s?.StatusType?.status_name?.toLowerCase() === 'active',
        )?.status_type_id || dropdowns.statusTypes[0]?.status_type_id || ''
    );
}

export default function AddContactsWizard() {
    const [step, setStep] = useState('search');
    const [selectedEntity, setSelectedEntity] = useState(null);
    const [dropdowns, setDropdowns] = useState(null);
    const [address, setAddress] = useState(null);
    const [contacts, setContacts] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [savedCount, setSavedCount] = useState(0);

    // Permissions step
    const [createdContacts, setCreatedContacts] = useState([]);
    const [addressItems, setAddressItems] = useState([]);
    const [accessMap, setAccessMap] = useState({});
    const [loadingItems, setLoadingItems] = useState(false);
    const [savingAccess, setSavingAccess] = useState(false);
    const [existingContacts, setExistingContacts] = useState([]);
    const [loadingExisting, setLoadingExisting] = useState(false);
    const [aiOpen, setAiOpen] = useState(false);

    // Load dropdowns on mount
    useEffect(() => {
        loadDropdownData().then(d => {
            setDropdowns(d);
            setAddress(blankContact(getActiveStatusId(d)));
        }).catch(() => toast.error('Failed to load form options'));
    }, []);

    // Load existing contacts when entity is selected
    useEffect(() => {
        if (!selectedEntity) {
            setExistingContacts([]);
            return;
        }
        const entityId = getEntityId(selectedEntity);
        setLoadingExisting(true);
        getAllContactsByEntity(entityId)
            .then(res => {
                const raw = Array.isArray(res?.data)
                    ? res.data
                    : Array.isArray(res?.data?.contacts)
                        ? res.data.contacts
                        : Array.isArray(res?.contacts)
                            ? res.contacts
                            : [];
                setExistingContacts(raw.map((c) => {
                    const contact = c?.Contact ?? c;
                    const addresses = contact?.ContactAddresses ?? c?.ContactAddresses ?? [];
                    const email = addresses.find((a) => a.address_type_id === 2)?.contact_address
                        ?? contact?.user?.email
                        ?? c?.email;
                    const phone = addresses.find((a) => a.address_type_id === 1 && a.is_default)?.contact_address
                        ?? contact?.user?.phone
                        ?? c?.phone;
                    return {
                        contact_id: c.contact_id ?? contact?.contact_id ?? contact?.id,
                        first_name: c.first_name ?? contact?.first_name ?? contact?.contact_first_name ?? '',
                        last_name: c.last_name ?? contact?.last_name ?? contact?.contact_last_name ?? '',
                        email,
                        phone,
                        user_id: c.user?.id ?? contact?.user?.id ?? undefined,
                        contact_type: c.ContactType?.type_name ?? contact?.ContactType?.type_name,
                    };
                }));
            })
            .catch(() => setExistingContacts([]))
            .finally(() => setLoadingExisting(false));
    }, [selectedEntity]);

    const update = (id, field, value) => {
        setContacts(prev => prev.map(c => (c.id === id ? { ...c, [field]: value } : c)));
    };

    const addContact = () => {
        if (!dropdowns) return;
        setContacts(prev => [...prev, blankContact(getActiveStatusId(dropdowns))]);
    };

    const removeContact = (id) => {
        if (contacts.length > 1) setContacts(prev => prev.filter(c => c.id !== id));
    };

    const validContacts = contacts.filter(
        c => c.contact_first_name.trim() && c.contact_last_name.trim() && c.email.trim(),
    );

    const handleSubmit = async () => {
        if (!selectedEntity || validContacts.length === 0) return;
        setSubmitting(true);
        try {
            const entityId = getEntityId(selectedEntity);
            
            // Map validContacts to the batch format required by the backend schema
            const batchData = validContacts.map(contact => ({
                data: {
                    attributes: {
                        first_name: contact.contact_first_name,
                        last_name: contact.contact_last_name,
                        contactType: {
                            contact_type_id: contact.contact_type_id || 1, // Default or from state
                        },
                        statusType: {
                            status_type_id: contact.status_type_id || 1, // Default or from state
                        },
                        emailAddresses: contact.email ? [{ email: contact.email }] : [],
                        phoneAddresses: contact.phone ? [{ phone: contact.phone }] : [],
                        target_entity_id: entityId,
                        create_user_account: contact.createUserAccount || false
                    },
                    relationships: {
                        entities: {
                            data: [{ id: entityId }]
                        }
                    }
                }
            }));
            console.log('batchData', batchData);
            const res = await crudsService.createBatchContact({batch_data: batchData});
            
            // Extract created contacts from response
            // Assuming response is an array of created contacts or contains a data array
            const createdContactsData = res?.data || res || [];
            const createdIdObjs = (Array.isArray(createdContactsData) ? createdContactsData : []).map(c => ({
                id: Number(c.id || c.attributes?.id),
                name: `${c.attributes?.first_name || ''} ${c.attributes?.last_name || ''}`.trim(),
            }));

            const createdIds = createdIdObjs.map(c => c.id);

            setSavedCount(createdIds.length);
            setCreatedContacts(createdIdObjs);
            toast.success(`${createdIds.length} contact(s) added!`);

            // Load address items for permissions step
            setLoadingItems(true);
            try {
                const itemsRes = await loadEntityAddressItems(entityId);
                const raw = itemsRes.data?.data || itemsRes.data || itemsRes;
                const items = (Array.isArray(raw) ? raw : []).map((i) => ({
                    ...(function () {
                        const attrValues = Array.isArray(i?.ItemAttributeValues) ? i.ItemAttributeValues : [];
                        const getAttr = (name) => {
                            const match = attrValues.find(
                                (a) =>
                                    String(a?.Attribute?.attribute_name || '').toLowerCase() === name.toLowerCase(),
                            );
                            return match?.value ? String(match.value) : '';
                        };
                        const netCapacity = getAttr('Net Capacity (Gallons)');
                        const capacity = getAttr('Capacity');
                        return {
                            contents: getAttr('Contents') || undefined,
                            capacity_gallons: netCapacity || capacity || undefined,
                        };
                    })(),
                    address_item_id: i.address_item_id ?? i.entity_address_item_id ?? i.id,
                    name: i.name ?? i.item_name ?? `Item ${i.address_item_id ?? i.id}`,
                    address_name: i.address_name ?? i?.Address?.name,
                    map_identifier: i?.map_identifier ?? undefined,
                    item_name: i?.Item?.name ?? undefined,
                    item_type_name: i?.Item?.ItemType?.type_name ?? undefined,
                    desired_state: i?.Item?.ItemDesiredState?.name ?? undefined,
                    street_address1: i?.Address?.street_address1 ?? undefined,
                    city: i?.Address?.city ?? undefined,
                    state: i?.Address?.state ?? undefined,
                    postal_code: i?.Address?.postal_code ?? undefined,
                }));
                setAddressItems(items);

                // Build access map: default write/write enabled for every contact × item
                const map = {};
                createdIdObjs.forEach(contact => {
                    items.forEach(item => {
                        map[`${contact.id}-${item.address_item_id}`] = {
                            access_type: 'write',
                            service_access_type: 'write',
                            enabled: true,
                        };
                    });
                });
                setAccessMap(map);
            } catch {
                // No items or failed — still go to permissions
                setAddressItems([]);
            } finally {
                setLoadingItems(false);
            }

            setStep('permissions');
        } catch (err) {
            toast.error(err.message || 'Failed to add contacts');
        } finally {
            setSubmitting(false);
        }
    };

    const handleSavePermissions = async (skip = false) => {
        if (skip || addressItems.length === 0) {
            setStep('done');
            return;
        }
        const entityId = getEntityId(selectedEntity);
        setSavingAccess(true);
        try {
            for (const contact of createdContacts) {
                const entries = Object.entries(accessMap)
                    .filter(([key, row]) => key.startsWith(`${contact.id}-`) && row.enabled)
                    .map(([key, row]) => ({
                        contact_id: contact.id,
                        address_item_id: Number(key.split('-')[1]),
                        access_type: row.access_type,
                        service_access_type: row.service_access_type,
                    }));
                if (entries.length > 0) {
                    await setContactDeviceAccess(contact.id, entityId, entries);
                }
            }
            toast.success('Permissions saved!');
            setStep('done');
        } catch (err) {
            toast.error(err.message || 'Failed to save permissions');
        } finally {
            setSavingAccess(false);
        }
    };

    const updateAccess = (key, field, value) =>
        setAccessMap(prev => ({ ...prev, [key]: { ...prev[key], [field]: value } }));

    const setAllAccessForContact = (contactId, enabled) => {
        setAccessMap(prev => {
            const next = { ...prev };
            Object.keys(next).forEach(key => {
                if (key.startsWith(`${contactId}-`)) {
                    next[key] = { ...next[key], enabled };
                }
            });
            return next;
        });
    };

    const resetWizard = () => {
        setStep('search');
        setSelectedEntity(null);
        setSavedCount(0);
        setCreatedContacts([]);
        setAddressItems([]);
        setAccessMap({});
        setExistingContacts([]);
        setAiOpen(false);
        if (dropdowns) setContacts([blankContact(getActiveStatusId(dropdowns))]);
    };

    const handleApplyContactPrefill = (candidates, selectedIds) => {
        if (loadingExisting) return [{ reason: 'Wait for existing contacts to finish loading' }];
        const result = applyContactCandidates(contacts, candidates, selectedIds, existingContacts);
        if (result.skipped.length) return result.skipped;
        setContacts(result.contacts);
        setStep('contacts');
        return [];
    };

    const contactTypes = dropdowns?.contactTypes ?? [];
    const statusOptions = dropdowns?.statusTypes ?? [];

    const darkCard = {
        backgroundColor: "rgb(38, 40, 51)",
        border: "1px solid #gray",
        borderRadius: 3,
    };

    const inputSx = {
        "& .MuiOutlinedInput-root": {
            backgroundColor: "rgb(38, 40, 51)",
            "& fieldset": { borderColor: "#gray" },
            "&:hover fieldset": { borderColor: "#4ADE80" },
            "&.Mui-focused fieldset": { borderColor: "#4ADE80" },
        },
        "& .MuiInputBase-input": {
            color: "#fff",
        },
        "& .MuiInputLabel-root": {
            color: "#9CA3AF",
        },
    };

    return (
        <MDBox sx={{ mx: 'auto' }}>
            {step !== 'done' && <StepBar current={step} STEPS={STEPS} />}

            {/* ── Step 1: Search entity ── */}
            {step === 'search' && (
                <MDBox sx={{ backgroundColor: 'rgb(38, 40, 51)', border: '1px solid #gray', borderRadius: '12px', p: 3 }}>
                    <MDTypography variant="h6" color="white" fontWeight="bold">Find the Entity</MDTypography>
                    <MDTypography variant="button" color="white" opacity={0.6} sx={{ display: 'block', mb: 3 }}>
                        Search for the customer or entity you want to add an address to.
                    </MDTypography>

                    <EntitySearchPicker
                        selectedEntity={selectedEntity}
                        onSelect={setSelectedEntity}
                    />

                    <MDBox mt={4} display="flex" justifyContent="flex-end">
                        <MDButton
                            disabled={!selectedEntity}
                            onClick={() => setStep('contacts')}
                            variant="gradient"
                            color="info"
                            sx={{ gap: 1 }}
                        >
                            Continue <i className="fa fa-chevron-right" />
                        </MDButton>
                    </MDBox>
                </MDBox>
            )}

            {/* ── Step 2: Contact forms ── */}
            {step === "contacts" && dropdowns && selectedEntity && (
                <MDBox sx={{ display: "flex", flexDirection: "column", gap: 4 }}>

                    {/* Header */}
                    <MDBox sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <MDTypography variant="body2" sx={{ color: "#9CA3AF" }}>
                            Adding contacts for{" "}
                            <MDBox component="span" sx={{ color: "#fff", fontWeight: 500 }}>
                                {getEntityDisplayName(selectedEntity)}
                            </MDBox>
                        </MDTypography>

                        <MDButton
                            onClick={addContact}
                            startIcon={<i className="fa-solid fa-plus" />}
                            sx={{
                                backgroundColor: "#4ADE80",
                                color: "#000",
                                fontWeight: 600,
                                "&:hover": { backgroundColor: "#22C55E" },
                            }}
                        >
                            Add Contact
                        </MDButton>

                        <MDButton
                            variant="outlined"
                            color="info"
                            startIcon={<AutoAwesomeOutlinedIcon />}
                            disabled={!selectedEntity || !dropdowns || loadingExisting || submitting}
                            onClick={() => setAiOpen(true)}
                            sx={{
                                color: "#fff",
                                borderColor: "#gray",
                                "&:hover": { borderColor: "#4ADE80", color: "#4ADE80" },
                            }}
                        >
                            AI Prefill
                        </MDButton>
                    </MDBox>

                    {/* Contact Forms */}
                    {contacts.map((c, idx) => (
                        <Card key={c.id} sx={{ ...darkCard, p: 3 }}>
                            <CardContent sx={{ p: 2 }}>
                                <MDBox sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                                    <MDTypography variant="h6" sx={{ color: "#fff" }}>
                                        Contact {idx + 1}
                                    </MDTypography>

                                    {contacts.length > 1 && (
                                        <IconButton onClick={() => removeContact(c.id)}>
                                            <i className="fa-solid fa-trash" style={{ color: "#f87171" }} />
                                        </IconButton>
                                    )}
                                </MDBox>

                                <MDBox component="form" className="space-y-4">

                                    {/* First + Last Name */}
                                    <MDBox display="flex" gap={2} mb={2}>
                                        <MDBox flex={1}>
                                            <MDTypography
                                                variant="caption"
                                                color="white"
                                                fontWeight="bold"
                                                sx={{ mb: 0.5, display: "block" }}
                                            >
                                                First Name <span style={{ color: "#F87171" }}>*</span>
                                            </MDTypography>
                                            <input
                                                type="text"
                                                value={c.contact_first_name}
                                                onChange={(e) =>
                                                    update(c.id, "contact_first_name", e.target.value)
                                                }
                                                style={{
                                                    width: "100%",
                                                    padding: "10px 12px",
                                                    backgroundColor: "rgb(38, 40, 51)",
                                                    border: "1px solid #gray",
                                                    borderRadius: "8px",
                                                    color: "white",
                                                    fontSize: "0.875rem",
                                                }}
                                            />
                                        </MDBox>

                                        <MDBox flex={1}>
                                            <MDTypography
                                                variant="caption"
                                                color="white"
                                                fontWeight="bold"
                                                sx={{ mb: 0.5, display: "block" }}
                                            >
                                                Last Name <span style={{ color: "#F87171" }}>*</span>
                                            </MDTypography>
                                            <input
                                                type="text"
                                                value={c.contact_last_name}
                                                onChange={(e) =>
                                                    update(c.id, "contact_last_name", e.target.value)
                                                }
                                                style={{
                                                    width: "100%",
                                                    padding: "10px 12px",
                                                    backgroundColor: "rgb(38, 40, 51)",
                                                    border: "1px solid #gray",
                                                    borderRadius: "8px",
                                                    color: "white",
                                                    fontSize: "0.875rem",
                                                }}
                                            />
                                        </MDBox>
                                    </MDBox>

                                    {/* Contact Type + Title */}
                                    <MDBox display="flex" gap={2} mb={2}>
                                        <MDBox flex={1}>
                                            <MDTypography
                                                variant="caption"
                                                color="white"
                                                fontWeight="bold"
                                                sx={{ mb: 0.5, display: "block" }}
                                            >
                                                Contact Type <span style={{ color: "#F87171" }}>*</span>
                                            </MDTypography>
                                            <select
                                                value={c.contact_type_id}
                                                onChange={(e) =>
                                                    update(c.id, "contact_type_id", e.target.value)
                                                }
                                                style={{
                                                    width: "100%",
                                                    padding: "10px 12px",
                                                    backgroundColor: "rgb(38, 40, 51)",
                                                    border: "1px solid #gray",
                                                    borderRadius: "8px",
                                                    color: "white",
                                                    fontSize: "0.875rem",
                                                }}
                                            >
                                                <option value="">— Select type —</option>
                                                {contactTypes.map((t) => (
                                                    <option key={t.contact_type_id} value={t.contact_type_id}>
                                                        {t.type_name}
                                                    </option>
                                                ))}
                                            </select>
                                        </MDBox>

                                        <MDBox flex={1}>
                                            <MDTypography
                                                variant="caption"
                                                color="white"
                                                fontWeight="bold"
                                                sx={{ mb: 0.5, display: "block" }}
                                            >
                                                Title
                                            </MDTypography>
                                            <input
                                                type="text"
                                                value={c.title}
                                                onChange={(e) => update(c.id, "title", e.target.value)}
                                                style={{
                                                    width: "100%",
                                                    padding: "10px 12px",
                                                    backgroundColor: "rgb(38, 40, 51)",
                                                    border: "1px solid #gray",
                                                    borderRadius: "8px",
                                                    color: "white",
                                                    fontSize: "0.875rem",
                                                }}
                                            />
                                        </MDBox>
                                    </MDBox>

                                    {/* Email + Phone */}
                                    <MDBox display="flex" gap={2} mb={2}>
                                        <MDBox flex={1}>
                                            <MDTypography
                                                variant="caption"
                                                color="white"
                                                fontWeight="bold"
                                                sx={{ mb: 0.5, display: "block" }}
                                            >
                                                Email
                                            </MDTypography>
                                            <input
                                                type="email"
                                                value={c.email}
                                                onChange={(e) => update(c.id, "email", e.target.value)}
                                                style={{
                                                    width: "100%",
                                                    padding: "10px 12px",
                                                    backgroundColor: "rgb(38, 40, 51)",
                                                    border: "1px solid #gray",
                                                    borderRadius: "8px",
                                                    color: "white",
                                                    fontSize: "0.875rem",
                                                }}
                                            />
                                        </MDBox>

                                        <MDBox flex={1}>
                                            <MDTypography
                                                variant="caption"
                                                color="white"
                                                fontWeight="bold"
                                                sx={{ mb: 0.5, display: "block" }}
                                            >
                                                Phone
                                            </MDTypography>
                                            <input
                                                type="text"
                                                value={c.phone}
                                                onChange={(e) => update(c.id, "phone", maskPhone(e.target.value))}
                                                style={{
                                                    width: "100%",
                                                    padding: "10px 12px",
                                                    backgroundColor: "rgb(38, 40, 51)",
                                                    border: "1px solid #gray",
                                                    borderRadius: "8px",
                                                    color: "white",
                                                    fontSize: "0.875rem",
                                                }}
                                            />
                                        </MDBox>
                                    </MDBox>

                                    {/* Status + Toggle */}
                                    <MDBox
                                        sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            mt: 3,
                                            pt: 2,
                                            borderTop: "1px solid #gray",
                                        }}
                                    >
                                        <MDBox width="250px">
                                            <MDTypography
                                                variant="caption"
                                                color="white"
                                                fontWeight="bold"
                                                sx={{ mb: 0.5, display: "block" }}
                                            >
                                                Status
                                            </MDTypography>
                                            <select
                                                value={c.contact_status_type_id}
                                                onChange={(e) =>
                                                    update(c.id, "contact_status_type_id", e.target.value)
                                                }
                                                style={{
                                                    width: "100%",
                                                    padding: "10px 12px",
                                                    backgroundColor: "rgb(38, 40, 51)",
                                                    border: "1px solid #gray",
                                                    borderRadius: "8px",
                                                    color: "white",
                                                    fontSize: "0.875rem",
                                                }}
                                            >
                                                {statusOptions.map((s) => (
                                                    <option
                                                        key={s?.status_type_id}
                                                        value={s?.status_type_id}
                                                    >
                                                        {s?.StatusType?.status_name}
                                                    </option>
                                                ))}
                                            </select>
                                        </MDBox>

                                        <MDBox display="flex" alignItems="center" gap={1}>
                                            <Switch
                                                checked={c.createUserAccount}
                                                onChange={() =>
                                                    update(c.id, "createUserAccount", !c.createUserAccount)
                                                }
                                                sx={{
                                                    "& .MuiSwitch-switchBase.Mui-checked": {
                                                        color: "#4ADE80",
                                                    },
                                                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                                        backgroundColor: "#4ADE80",
                                                    },
                                                }}
                                            />
                                            <MDTypography variant="caption" color="white">
                                                Create app login
                                            </MDTypography>
                                        </MDBox>
                                    </MDBox>

                                </MDBox>
                            </CardContent>
                        </Card>
                    ))}

                    {/* Navigation */}
                    <MDBox mt={4} display="flex" justifyContent="space-between">
                        <MDButton
                            onClick={() => setStep('search')}
                            variant="outlined"
                            color="secondary"
                            sx={{ gap: 1 }}
                        >
                            <i className="fa fa-chevron-left" /> Back
                        </MDButton>

                        <MDButton
                            disabled={validContacts.length === 0}
                            onClick={() => setStep("review")}
                            variant="gradient"
                            color="info"
                            sx={{ gap: 1 }}
                        >
                            Review ({validContacts.length})
                        </MDButton>
                    </MDBox>
                </MDBox>
            )}

            {/* ── Step 3: Review ── */}
            {step === "review" && (
                <Card sx={{ ...darkCard, p: 4 }}>
                    <MDTypography variant="h6" sx={{ color: "#fff", mb: 3 }}>
                        Review & Confirm
                    </MDTypography>

                    <MDBox sx={{ mb: 3 }}>
                        {validContacts.map((c) => (
                            <MDBox
                                key={c.id}
                                sx={{
                                    display: "flex",
                                    gap: 2,
                                    alignItems: "center",
                                    mb: 2,
                                }}
                            >
                                <i className="fa-solid fa-users" style={{ color: "#4ADE80" }} />
                                <MDBox>
                                    <MDTypography sx={{ color: "#fff", fontWeight: 500 }}>
                                        {c.contact_first_name} {c.contact_last_name}
                                    </MDTypography>
                                    <MDTypography variant="caption" sx={{ color: "#9CA3AF" }}>
                                        {c.email}
                                    </MDTypography>
                                </MDBox>
                            </MDBox>
                        ))}
                    </MDBox>

                    <MDBox mt={4} display="flex" justifyContent="space-between">
                        <MDButton
                            onClick={() => setStep("contacts")}
                            variant="outlined"
                            color="secondary"
                            sx={{ gap: 1 }}
                        >
                            <i className="fa fa-chevron-left" /> Edit
                        </MDButton>

                        <MDButton
                            onClick={handleSubmit}
                            variant="gradient"
                            color="success"
                            sx={{ gap: 1 }}
                        >
                            {submitting ? (
                                <><i className="fa fa-spinner fa-spin" /> Saving…</>
                            ) : (
                                <>Confirm & Save <i className="fa fa-chevron-right" /></>
                            )}
                        </MDButton>
                    </MDBox>
                </Card>
            )}

            {/* ── Step 4: Permissions ── */}
            {step === "permissions" && selectedEntity && (
                <MDBox sx={{ display: "flex", flexDirection: "column", gap: 4 }}>

                    {/* Header Card */}
                    <Card sx={{ ...darkCard, p: 3 }}>
                        <MDBox sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <MDBox
                                sx={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: 2,
                                    backgroundColor: "#49a3f1",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <i className="fa-solid fa-lock" style={{ color: "#4ADE80" }} />
                            </MDBox>

                            <MDBox>
                                <MDTypography sx={{ color: "#fff", fontWeight: 600 }}>
                                    Set Access Permissions
                                </MDTypography>
                                <MDTypography variant="caption" sx={{ color: "#9CA3AF" }}>
                                    Choose which items each new contact can access.
                                </MDTypography>
                            </MDBox>
                        </MDBox>
                    </Card>

                    {/* Loading */}
                    {loadingItems && (
                        <MDBox sx={{ textAlign: "center", py: 6 }}>
                            <i
                                className="fa-solid fa-spinner"
                                style={{ fontSize: 24, color: "#4ADE80", animation: "spin 1s linear infinite" }}
                            />
                        </MDBox>
                    )}

                    {/* No Items */}
                    {!loadingItems && addressItems.length === 0 && (
                        <Card sx={{ ...darkCard, p: 4, textAlign: "center" }}>
                            <MDTypography sx={{ color: "#9CA3AF" }}>
                                No address items found for this entity.
                            </MDTypography>
                            <MDTypography variant="caption" sx={{ color: "#6B7280" }}>
                                You can assign access later from the entity page.
                            </MDTypography>
                        </Card>
                    )}

                    {/* Permissions Per Contact */}
                    {!loadingItems &&
                        addressItems.length > 0 &&
                        createdContacts.map((contact) => (
                            <Card key={contact.id} sx={{ ...darkCard, p: 3 }}>
                                <CardContent sx={{ p: 0 }}>

                                    {/* Contact Header */}
                                    <MDBox
                                        sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            m: 2
                                        }}
                                    >
                                        <MDTypography sx={{ color: "#fff", fontWeight: 600 }}>
                                            {contact.name}
                                        </MDTypography>

                                        <MDBox sx={{ display: "flex", gap: 1 }}>
                                            <MDButton
                                                size="small"
                                                onClick={() => setAllAccessForContact(contact.id, true)}
                                                sx={{
                                                    border: "1px solid #49a3f1",
                                                    color: "#4ADE80",
                                                }}
                                            >
                                                Select All
                                            </MDButton>

                                            <MDButton
                                                size="small"
                                                onClick={() => setAllAccessForContact(contact.id, false)}
                                                sx={{
                                                    border: "1px solid #3A3A3A",
                                                    color: "#9CA3AF",
                                                }}
                                            >
                                                Clear
                                            </MDButton>
                                        </MDBox>
                                    </MDBox>

                                    {/* Item List */}
                                    <MDBox sx={{ display: "flex", flexDirection: "column", gap: 2, px: 2 }}>
                                        {addressItems.map((item) => {
                                            const key = `${contact.id}-${item.address_item_id}`;
                                            const row = accessMap[key];
                                            if (!row) return null;

                                            return (
                                                <MDBox
                                                    key={key}
                                                    sx={{
                                                        backgroundColor: "rgb(38, 40, 51)",
                                                        borderRadius: 2,
                                                        p: 2,
                                                        display: "flex",
                                                        flexDirection: { xs: "column", md: "row" },
                                                        justifyContent: "space-between",
                                                        alignItems: { md: "center" },
                                                        gap: 2,
                                                    }}
                                                >
                                                    {/* Left Side */}
                                                    <MDBox sx={{ display: "flex", gap: 2, flex: 1 }}>
                                                        <Switch
                                                            checked={row.enabled}
                                                            onChange={() =>
                                                                updateAccess(key, "enabled", !row.enabled)
                                                            }
                                                            sx={{
                                                                "& .MuiSwitch-switchBase.Mui-checked": {
                                                                    color: "#4ADE80",
                                                                },
                                                                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                                                                {
                                                                    backgroundColor: "#4ADE80",
                                                                },
                                                            }}
                                                        />

                                                        <MDBox>
                                                            <MDTypography
                                                                sx={{
                                                                    color: row.enabled ? "#fff" : "#6B7280",
                                                                    fontWeight: 500,
                                                                }}
                                                            >
                                                                {item.name}
                                                            </MDTypography>

                                                            {(item.item_name ||
                                                                item.item_type_name ||
                                                                item.map_identifier) && (
                                                                    <MDTypography
                                                                        variant="caption"
                                                                        sx={{ color: "#fff", display: "block" }}
                                                                    >
                                                                        {[item.item_name,
                                                                        item.item_type_name,
                                                                        item.map_identifier
                                                                            ? `Map: ${item.map_identifier}`
                                                                            : "",
                                                                        ]
                                                                            .filter(Boolean)
                                                                            .join(" • ")}
                                                                    </MDTypography>
                                                                )}
                                                        </MDBox>
                                                    </MDBox>

                                                    {/* Access Selectors */}
                                                    {row.enabled && (
                                                        <MDBox sx={{ display: "flex", gap: 2 }}>
                                                            <TextField
                                                                select
                                                                size="medium"
                                                                label="Data"
                                                                value={row.access_type}
                                                                onChange={(e) =>
                                                                    updateAccess(key, "access_type", e.target.value)
                                                                }
                                                                sx={{ ...inputSx, width: 120 }}
                                                            >
                                                                <MenuItem value="read">Read</MenuItem>
                                                                <MenuItem value="write">Write</MenuItem>
                                                            </TextField>

                                                            <TextField
                                                                select
                                                                size="medium"
                                                                label="Service"
                                                                value={row.service_access_type}
                                                                onChange={(e) =>
                                                                    updateAccess(
                                                                        key,
                                                                        "service_access_type",
                                                                        e.target.value
                                                                    )
                                                                }
                                                                sx={{ ...inputSx, width: 120 }}
                                                            >
                                                                <MenuItem value="read">Read</MenuItem>
                                                                <MenuItem value="write">Write</MenuItem>
                                                            </TextField>
                                                        </MDBox>
                                                    )}
                                                </MDBox>
                                            );
                                        })}
                                    </MDBox>
                                </CardContent>
                            </Card>
                        ))}

                    {/* Footer Navigation */}
                    <MDBox sx={{ display: "flex", justifyContent: "space-between" }}>
                        <MDButton
                            onClick={() => handleSavePermissions(true)}
                            variant="outlined"
                            color="secondary"
                            sx={{ gap: 1 }}
                        >
                            Skip
                        </MDButton>

                        <MDButton
                            disabled={savingAccess}
                            onClick={() => handleSavePermissions(false)}
                            variant="gradient"
                            color="success"
                            sx={{ gap: 1 }}
                        >
                            {savingAccess ? (
                                <><i className="fa fa-spinner fa-spin" /> Saving…</>
                            ) : (
                                <>Save Permissions <i className="fa fa-chevron-right" /></>
                            )}
                        </MDButton>
                    </MDBox>
                </MDBox>
            )}

            {aiOpen && step === 'contacts' && selectedEntity && (
              <ContactPrefillDialog 
                key={getEntityId(selectedEntity)}
                onClose={() => setAiOpen(false)} 
                onApply={handleApplyContactPrefill} 
              />
            )}

            {/* ── Step 5: Done ── */}
            {step === "done" && (
                <Card sx={{ ...darkCard, p: 6, textAlign: "center" }}>
                    <i
                        className="fa-solid fa-circle-check"
                        style={{ fontSize: 60, color: "#4ADE80", marginBottom: 16 }}
                    />
                    <MDTypography variant="h5" sx={{ color: "#fff", mb: 1 }}>
                        Contacts Added!
                    </MDTypography>

                    <MDTypography sx={{ color: "#9CA3AF", mb: 3 }}>
                        {savedCount} contact(s) linked successfully.
                    </MDTypography>

                    <MDBox sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
                        <MDButton
                            onClick={resetWizard}
                            variant="contained"
                            color="secondary"
                            size="small"
                        >
                            Add More
                        </MDButton>

                        <MDButton
                            component={Link}
                            to="/onboarding"
                            variant="outlined"
                            color="white"
                            size="small"
                        >
                            Back to Onboarding
                        </MDButton>
                    </MDBox>
                </Card>
            )}
        </MDBox>
    );
}
